<template>
  <div>
    <main id="main" v-if="pageData" class="productpage grid-productpage">
      <ShopBlock
        :ingress="pageData.Ingress"
        :description="pageData.ProductDescription"
        :configurationProducts="pageData.ConfigurationProducts"
        :hideColorAndSize="hideColorAndSize"
        :model="pageData.ProductViewModel"
        :variantInUrl="variantInUrl"
        :outlet="outlet"
        :archived="archived"
        :sizeGuideLink="sizeGuide"
        @ready="shopBlockReady"
        @color-code-change="colorUpdate"
      />
      <transition name="fade">
        <div
          v-if="loader"
          class="w-100 d-flex justify-content-center m-t-36 m-b-36 -p-absolute"
        >
          <SvgComponent
            :icon="{
              name: 'Loading',
              color: Globals.colors.grayLight,
              size: 50,
            }"
          />
        </div>
      </transition>
      <ul ref="nav" class="accordion">
        <li
          v-for="block in blocks.filter((b) => !b.loadError)"
          :key="`wrapper-${block.component}`"
          :id="block.component"
          :class="[`-${block.component}`]"
          v-show="!block.loadError"
        >
          <ProductSection
            v-if="!block.loadError"
            :class="[block.class, { '-loading': !block.ready }]"
            :key="`section-${block.component}`"
            :id="block.Component"
            :reference="block.component"
            :title="
              block.title ||
              insertString('ProductPage', 'BlockTitle', block.component)
            "
            :defaultOpen="block.open"
            :collapseOnOpen="false"
            :noPadding="true"
          >
            <component
              :key="block.component"
              :is="block.component"
              slot="sectioncontent"
              slot-scope="slotProps"
              :ready="slotProps.ready || !isMobile"
              :title="
                isMobile || !block.hideDesktopTitle ? slotProps.title : ''
              "
              :noTitle="isMobile"
              v-bind="block.bindObject"
              :code="code"
              :activeColorCode="variantInUrl"
              @ready="setReadyState(block)"
              @load-error="hideBlock(block)"
            />
          </ProductSection>
        </li>
      </ul>
      <LoyaltyBanner v-if="outlet && !isLoyalty" />
    </main>
    <Modal
      :visible="addToBagOverlay"
      :altDestination="'modal-shopblock-destination'"
      :title="insertString('Product', 'ProductAddedToBag')"
      :gridCols="$mqAliases.xl ? 6 : $mqAliases.md || $mqAliases.lg ? 6 : 12"
      :scrollLock="true"
      :verticalCenter="true"
      :compact="true"
      @close="closeAddToBagOverlay"
    >
      <AddedToBagSummary />
    </Modal>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import { eventBus } from '@/Scripts/lib/helper/eventBus.js';
import { PRODUCTCODES } from '@/Scripts/constants.js';
import Api from '@/Scripts/api/api';
import stringHelper from '@/Scripts/lib/helper/stringHelper.js';
import BazaarvoiceHelper from '@/Scripts/lib/helper/bazaarvoice';
// import { BlockStyle } from '@/Scripts/lib/helper/blockHelper.js'; // under development
import pageLoader from '@/Scripts/lib/helper/pageLoader.js';
import ShopBlock from '@/Scripts/components/pages/ProductV2/Blocks/ShopBlock.vue';
import ProductSection from '@/Scripts/components/pages/ProductV2/Section.vue';
import SvgComponent from '@/Scripts/components/shared/svg/Svg.vue';
import Modal from '@/Scripts/components/shared/Modal/ModalNew.vue';
import AddedToBagSummary from '@/Scripts/components/layout/Bag/Assets/AddedToBagSummary.vue';

// standard blocks
const ProductsInAction = () => import(/* webpackChunkName: "ProductsInActionComponent" */ '@/Scripts/components/shared/ProductsInAction/ProductsInAction.vue');
const ProductPerformance = () => import(/* webpackChunkName: "ProductsInActionComponent" */ '@/Scripts/components/pages/ProductV2/Blocks/Performance.vue');
const ProductDetails = () => import(/* webpackChunkName: "ProductDetails" */ '@/Scripts/components/pages/ProductV2/Blocks/Details.vue');
const ProductTests = () => import(/* webpackChunkName: "ProductTests" */ '@/Scripts/components/pages/ProductV2/Blocks/Tests.vue');
const ProductFitSize = () => import('@/Scripts/components/pages/ProductV2/Blocks/SizingBlock/SizingBlock.vue');
const ProductRecommender = () => import(/* webpackChunkName: "Recommender" */ '@/Scripts/components/shared/Recommender/Recommender.vue');
const ProductReviews = () => import('@/Scripts/components/pages/ProductV2/Blocks/Reviews.vue');
const LoyaltyBanner = () => import(/* webpackChunkName: "LoyaltyPopup" */ '@/Scripts/components/shared/LoyaltyPopup/LoyaltyPopup.vue');
const FabricWrapperBlock = () => import(/* webpackChunkName: "ProductDetails" */ '@/Scripts/components/pages/ProductV2/Blocks/FabricWrapperBlock.vue');
const ProductHiggsBlock = () => import(/* webpackChunkName: "ProductDetails" */ '@/Scripts/components/blocks/ProductHiggsBlock.vue');
const FactoryBlock = () => import(/* webpackChunkName: "FactoryBlock" */ '@/Scripts/components/blocks/FactoryBlock.vue');
const TestimonialSliderBlock = () => import(/* webpackChunkName: "TestimonialSliderBlock" */ '@/Scripts/components/blocks/TestimonialSliderBlock.vue');
const ProductFeatureBlock = () => import(/* webpackChunkName: "ProductFeatureBlock" */ '@/Scripts/components/blocks/ProductFeatureBlock.vue');
const ImageCollectorSliderBlock = () => import('@/Scripts/components/blocks/ContentSliderBlock.vue');

// custom blocks
const ProductTechBlock = () => import(/* webpackChunkName: "ProductTechBlock" */ '@/Scripts/components/blocks/ProductTechBlock.vue');

export default {
  name: 'ProductPage',
  mixins: [pageLoader],
  components: {
    ShopBlock,
    ProductSection,
    SvgComponent,
    Modal,
    AddedToBagSummary,

    ProductsInAction,
    ProductPerformance,
    ProductDetails,
    ProductFitSize,
    ProductRecommender,
    ProductReviews,
    LoyaltyBanner,
    ProductTechBlock,
    FabricWrapperBlock,
    ProductHiggsBlock,
    FactoryBlock,
    TestimonialSliderBlock,
    ProductFeatureBlock,
    ProductTests,
    ImageCollectorSliderBlock

  },

  data () {
    return {
      // BV temp
      reviews: null,
      questions: null,
      addToBagOverlay: false,
      sizeGuide: false,
      loader: true,
      readyBlockCount: 0,
      pageData: window.PageData,
      variantInUrl: stringHelper.getURLParameter('color'),
      productStoreAvailability: null,
      loadedBlocks: [],
      blockData: [

        {
          order: 0,
          api: 'GetActionImagesForProduct',
          component: 'ProductsInAction',
          parser: 'parseProductsInAction',
          prop: 'images',
          hideDesktopTitle: true
        },
        {
          order: 1,
          component: 'ImageCollectorSliderBlock',
          open: true,
          parser: 'parseImageCollectorSliderBlock'
        },
        {
          order: 1,
          api: 'UseAndPerformance',
          component: 'ProductPerformance',
          parser: 'noParse',
          prop: 'productData'
        },
        {
          order: 2,
          api: 'ProductDetails',
          component: 'ProductDetails',
          parser: 'parseObject',
          template: {
            csr: 'Csr',
            modelIsWearing: 'ModelIsWearing',
            modelNumber: 'ModelNumber',
            technicalDetails: 'TechnicalDetails',
            fabrics: 'FabricTechnologies',
            weight: 'WeightInGrams',
            sliderItems: 'ProductDetailSliders'
          }
        },
        {
          order: 3,
          component: 'ProductHiggsBlock',
          parser: 'higgData'
        },
        {
          order: 4,
          component: 'ProductFitSize',
          parser: 'fitSizeData'
        },
        {
          order: 5,
          api: 'Fabrics',
          component: 'FabricWrapperBlock',
          open: true,
          parser: 'parseObject',
          template: {
            blocks: 'Fabrics'
          }
        },
        {
          order: 6,
          component: 'FactoryBlock',
          open: false,
          parser: 'parseFactoryBlock'
        },
        {
          order: 6,
          component: 'TestimonialSliderBlock',
          open: true,
          parser: 'parseStoryBlock'
        },
        {
          order: 6,
          component: 'ProductFeatureBlock',
          open: true,
          parser: 'parseProductFeatureBlock'
        },
        {
          order: 7,
          api: 'Recommendations',
          component: 'ProductRecommender',
          parser: 'parseStringToObject',
          open: true,
          refresh: 'colorChange',
          template: {
            productData: 'Products'
          },
          params: `&color=${this.variantInUrl}`
        },
        {
          order: 90,
          component: 'ProductTests',
          open: true,
          parser: 'parseProductTests'
        },
        {
          order: 100,
          component: 'ProductReviews',
          open: true,
          init: 'bazaarvoiceInit'
        }
      ]
    };
  },
  computed: {
    ...mapState({
      isLoyalty: state => state.appContext.isLoyalty,
      isLoggedIn: state => state.appContext.isLoggedIn
    }),
    isMobile () {
      return this.$mqAliases.xs || this.$mqAliases.sm;
    },
    hideColorAndSize () {
      // Ulysse watch page in basic mode (limited actions)
      return this.code === PRODUCTCODES.ULYSSE;
    },
    code () {
      if (!this.pageData) {
        return null;
      }
      return this.pageData.ProductViewModel.Code;
    },
    variantIsActive () {
      const startColor = this.variantInUrl || this.pageData.ProductViewModel.MainColor;
      return this.pageData.ProductViewModel.ActiveVariants.some(v => v.ColorCode === startColor);
    },
    outlet () {
      if (window.location.href.match(/\/o\//)) {
        return true;
      }
      return !this.variantIsActive && this.pageData.ProductViewModel.OutletVariants.some(v => v.ColorCode === this.variantInUrl);
    },
    archived () {
      if (!this.pageData.ProductViewModel.ArchivedVariants.length || (this.pageData.ProductViewModel.ArchivedVariants.length && this.pageData.ProductViewModel.ArchivedVariants.some(v => v.ColorCode === this.pageData.ProductViewModel.OutletVariants.ColorCode))) {
        return false;
      }
      if (window.location.href.match(/\/archive\//)) {
        return true;
      }
      return !this.variantIsActive && this.pageData.ProductViewModel.ArchivedVariants.some(v => v.ColorCode === this.variantInUrl);
    },
    blocks () {
      const validBlocks = this.loadedBlocks.filter(obj => obj.data !== null);
      return validBlocks.sort((a, b) =>
        a.order > b.order ? 1 : b.order > a.order ? -1 : 0
      );
    }
  },
  methods: {
    closeAddToBagOverlay () {
      this.addToBagOverlay = false;
      setTimeout(() => {
        document.getElementById('addToBagButton').focus();
      }, 330);
    },
    hideBlock (block) {
      this.$set(block, 'loadError', true);
      this.countBlocks();
    },
    setReadyState (block) {
      if (block.component === 'ProductFitSize') {
        this.sizeGuide = true;
      }
      this.$set(block, 'ready', true);
      this.countBlocks();
    },
    countBlocks () {
      this.readyBlockCount++;
      if (this.readyBlockCount >= this.blocks.length) {
        this.loader = false;
      }
    },
    colorUpdate (color) {
      this.variantInUrl = color;
      history.replaceState(
        {},
        '',
        stringHelper.updateUrlParameter(
          window.location.href,
          'color',
          color
        )
      );
    },
    // START LOADING BLOCKS WHEN SHOPBLOCK IS READY
    shopBlockReady () {
      this.$_removePageLoader();
      this.blockData.filter(obj => !obj.disabled).forEach(block => {
        if (block.api) {
          // load blockdata from API
          this.loadBlockData(block);
        } else if (block.parser) {
          // blockdata allready exist on pageload
          this[block.parser](block);
        } else if (block.init) {
          this.addBlock(block);
          this[block.init]();
        } else if (block.static) {
          this.addBlock(block);
        }
      });
    },
    async loadBlockData (block) {
      const path = '/api/product/';
      const params = block.params || '';
      try {
        const response = await Api.getContentByFriendlyUrl(`${path}${block.api}?code=${this.code}${params}`);

        this[block.parser](response.data, block);
        if (block.triggerOnReady && this[block.triggerOnReady]) {
          this[block.triggerOnReady]();
        }
      } catch (e) {
        console.log(e);
      }
    },
    addBlock (block, bindObject, title, classes) {
      const component = block.component;
      const order = block.order;
      const open = block.open;
      this.loadedBlocks.push({
        order,
        title,
        component,
        bindObject,
        open,
        hideDesktopTitle: block.hideDesktopTitle,
        ready: false,
        loadError: false,
        class: classes
      });
    },
    // PARSERS
    noParse (data, block) {
      this.addBlock(block, {
        [block.prop]: data
      });
    },
    parseStringToObject (data, block) {
      this.parseObject(JSON.parse(data), block);
    },
    parseObject (data, block) {
      const bindObject = {};
      for (const [key, value] of Object.entries(block.template)) {
        bindObject[key] = data[value];
      }
      this.addBlock(block, bindObject);
    },
    parseProductsInAction (data, block) {
      if (data.length) {
        const bindObject = { [block.prop]: data, productsInFooterOfSlideshow: true };
        this.addBlock(block, bindObject);
      }
    },
    fitSizeData (block) {
      this.addBlock(block, {
        productData: {
          code: this.pageData.ProductViewModel.Code,
          category: this.pageData.ProductViewModel.Category
        }
      });
    },
    higgData (block) {
      this.addBlock(block, {
        productData: {
          code: this.pageData.ProductViewModel.Code,
          name: this.pageData.ProductViewModel.Name
        }
      });
    },
    parseFactoryBlock (block) {
      if (this.pageData.ContentArea && this.pageData.ContentArea.length && this.pageData.ContentArea[0].FactoryName) {
        this.addBlock(block, {
          model: this.pageData.ContentArea[0]
        });
      }
    },

    parseImageCollectorSliderBlock (block) {
      if (this.pageData.ContentArea && this.pageData.ContentArea.length && this.pageData.ContentArea[0].SliderType > -1) {
        this.addBlock(block, {
          model: this.pageData.ContentArea[0]
        });
      }
    },
    parseStoryBlock (block) {
      if (this.pageData.ContentArea && this.pageData.ContentArea.length && this.pageData.ContentArea[0].Items) {
        this.addBlock(block, {
          model: this.pageData.ContentArea[0]
        });
      }
    },
    parseProductFeatureBlock (block) {
      if (this.pageData.ContentArea && this.pageData.ContentArea.length && this.pageData.ContentArea[0].TechnicalDetails) {
        this.addBlock(block, {
          model: this.pageData.ContentArea[0]
        });
      }
    },
    parseProductTests (block) {
      if (this.pageData.ProductTestContentArea && this.pageData.ProductTestContentArea.length) {
        this.addBlock(block, {
          model: this.pageData.ProductTestContentArea,
          productData: {
            code: this.pageData.ProductViewModel.Code,
            name: this.pageData.ProductViewModel.Name
          }
        });
      }
    },

    async loadCustomBlocks (blockref, index) {
      try {
        const path = '/api/product/blockdata';
        const response = await Api.getContentByFriendlyUrl(`${path}?blockid=${blockref}`);
        const customBlock = {
          order: index,
          component: this.mapCustomBlock(blockref)
        };
        this.addBlock(customBlock, this.getCustomBindObject(blockref, response.data), response.data.Title, '-custom');
      } catch (e) {
        console.log(e);
      }
    },
    mapCustomBlock (blockref) {
      return {
        SleepingbagTechBlock: 'ProductTechBlock',
        SkiTechBlock: 'ProductTechBlock',
        TentTechBlock: 'ProductTechBlock'
      }[blockref];
    },

    getCustomBindObject (blockref, data) {
      if (blockref === 'SleepingbagTechBlock') {
        let temp = '10';
        const regex = /(\d+(?:\.\d*)?|\.\d+)\s+(\S+)C/i;
        const match = this.pageData.ProductViewModel.Name.match(regex);
        if (match && match.length === 3) {
          temp = match[2];
        }
        return {
          model: data,
          preSelectGender: this.pageData.ProductViewModel.Gender,
          preSelectTemp: temp
        };
      } else if (blockref === 'SkiTechBlock') {
        return {
          model: data,
          preSelectConcept: this.pageData.ProductViewModel.Concept
        };
      } else if (blockref === 'TentTechBlock') {
        return {
          model: data,
          preSelectConcept: this.pageData.ProductViewModel.Concept
        };
      }
      return { model: data };
    },
    bazaarvoiceInit () {
      BazaarvoiceHelper.addRatingOnProduct(this.pageData.ProductViewModel.Code);
    }
  },

  async created () {
    this.pageData = window.PageData;
    // Hot fix for missing variant arrays
    if (!('OutletVariants' in this.pageData.ProductViewModel)) {
      this.pageData.ProductViewModel.OutletVariants = [];
    }
    if (!('ArchivedVariants' in this.pageData.ProductViewModel)) {
      this.pageData.ProductViewModel.ArchivedVariants = [];
    }

    // Order of standardblocks and customblocks
    if (this.pageData.BlocksToDisplay) {
      this.pageData.BlocksToDisplay.forEach((blockref, index) => {
        const block = this.blockData.find((b) => b.component === blockref);
        if (block) {
          // order standard blocks
          block.order = index;
          if (block.fallBackComponent) {
            const fallbackBlock = this.blockData.find((b) => b.component === block.fallBackComponent);
            if (fallbackBlock) {
              fallbackBlock.order = index;
            }
          }
        } else {
          // get customblocks
          this.loadCustomBlocks(blockref, index);
        }
      });
    }
    eventBus.$on('on-added-to-bag', () => {
      this.addToBagOverlay = true;
    });
  },
  destroyed () {
    this.$set(this.loadedBlocks, []);
  }

};
</script>

<style lang="scss" src="@/scss/objects/objects.grid-productpage.scss"></style>
<style lang="scss" src="@/scss/components/accordion/components.accordion.scss"></style>

<style lang="scss">
@import "@/scss/defaults.scss";

@mixin no-expand() {
  h2, h3 {
    @include make-container();
    @include make-container-max-widths();
    background-color: transparent;

    button {
      padding-left: 0 !important;
      padding-right: 0 !important;
      border-width: 0 !important;
      pointer-events: none;
      cursor: default;

      .arrow {
        display: none;
      }
    }
  }
}

.productpage {
  .products-in-action {
    @include media-breakpoint-up(md) {
      padding-bottom: 0;
    }
  }
  // Container that holds all the overlays.
  .overlay-bounding-box {
    grid-row: 1 / 2;
    grid-column: 1 / 2;
    position: relative;
    margin: 0;
    pointer-events: none; // Makes sure the container itself does not prevent clicks and swipes on photos.
  }
  .product-section {
    margin: 0;
    background-color: $white;
    opacity: 1;
    transition: opacity 0.1s;
    &.-loading {
      opacity: 0;
    }

    h2 {
      @include media-breakpoint-down(sm) {
        background-color: $white;
      }

      button {
        @include font-size(body, xs);
        border-bottom-width: 0 !important;

        @include media-breakpoint-up(md) {
          @include font-size(larger, md);
        }

        &[aria-expanded="true"] {
          color: $black;
        }
      }
    }

    @include media-breakpoint-up(md) {
      padding-top: 48px;
      padding-bottom: 72px;
      border-bottom: 1px solid $gray;
      &.-custom {
        padding-bottom: 0;
      }
    }

    &.-no-expand {
      border-top: 1px solid $gray;
    }

    &-wrapper {
      background-color: $white;

      &.-transparent {
        background-color: transparent;
      }

      @include media-breakpoint-up(md) {
        display: block !important;
      }
    }

    &-content {
      @include media-breakpoint-down(sm) {
        padding-top: 0;
        padding-bottom: 36px;
      }
      h2:not(.product-heading) {
        padding: 0;
        margin-top: -6px;
        @include font-size(larger, md);
      }
    }
  }

  // first section element
  li:first-of-type {
    .product-section {
      @include media-breakpoint-up(md) {
        margin-top: 48px;
        &:not(.-custom) {
          padding-top: 0;
        }
      }
    }
  }

  .-ProductFeatureBlock .product-section-wrapper {
    @include media-breakpoint-down(sm) {
      padding-top: 18px;
    }
  }
  .-ProductFeatureBlock .product-section-wrapper,
  .-ProductFeatureBlock .product-section {
    background-color: #000000;
  }

  // .-FactoryBlock,
  .-TestimonialSliderBlock,
  .-FabricWrapperBlock {
    .product-section {
      padding: 0 !important;
    }
  }

  .-TestimonialSliderBlock,
  .-FabricWrapperBlock,
  .-ProductGoreModule,
  .-ProductExperience {
    .product-section {
      .product-section-wrapper {
        max-width: none;
        margin: 0;
        padding: 0;
      }
    }
  }

  .-FactoryBlock {
    .product-section {
      .product-section-wrapper {
        @include media-breakpoint-down(sm) {
          max-width: none;
          margin: 0;
          padding: 0;
        }
      }
    }
  }

  .-ProductTests,
  .-ProductExperience,
  .-ProductRecommender {
    .product-section {
      width: 100%;
      overflow-x: hidden;
    }
  }
  @include media-breakpoint-down(sm) {
    .-ProductRecommender {
      .product-section-wrapper {
        padding: 0;
      }
    }
    .-ImageCollectorSliderBlock .generic-slider-wrapper {
      padding-left: 0 !important;
      padding-right: 0 !important;
      .video-overlay-click {
        display:none;
      }
    }
  }
  .-ImageCollectorSliderBlock .row-header h2 {
    padding-bottom: 18px;
  }
}
</style>
